import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingCart, Gift, Clock, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Checkout Exit Intent Popup
 * Shows when user tries to leave the checkout page or is inactive
 * Configurable from admin panel
 */
const CheckoutExitPopup = ({ 
  cartTotal, 
  onContinue, 
  onApplyDiscount,
  hasItems = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch popup settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/marketing/popup-settings/public`);
        setSettings(res.data.checkout_exit_popup);
        setSettingsLoaded(true);
      } catch (error) {
        // Use defaults
        setSettings({
          enabled: true,
          headline: "Wait! You're So Close!",
          body: "Complete your order now and get a special discount.",
          cta_text: "Complete My Order",
          offer_type: "percentage",
          offer_value: 5,
          offer_code: "CHECKOUT5",
          inactivity_seconds: 60
        });
        setSettingsLoaded(true);
      }
    };
    fetchSettings();
  }, []);

  // Exit intent detection (mouse leaving viewport)
  const handleMouseLeave = useCallback((e) => {
    if (!settings?.enabled || triggered || !hasItems) return;
    
    // Only trigger when mouse leaves through the top of the viewport
    if (e.clientY <= 0) {
      const dismissed = sessionStorage.getItem('checkout_exit_dismissed');
      if (!dismissed) {
        setIsOpen(true);
        setTriggered(true);
        trackImpression();
      }
    }
  }, [triggered, settings, hasItems]);

  // Inactivity detection
  useEffect(() => {
    if (!settingsLoaded || !settings?.enabled || triggered || !hasItems) return;

    const dismissed = sessionStorage.getItem('checkout_exit_dismissed');
    if (dismissed) return;

    // Setup exit intent listener
    document.addEventListener('mouseleave', handleMouseLeave);

    // Setup inactivity timer
    let inactivityTimer;
    let lastActivity = Date.now();
    
    const resetTimer = () => {
      lastActivity = Date.now();
    };

    const checkInactivity = () => {
      const inactiveSeconds = (Date.now() - lastActivity) / 1000;
      if (inactiveSeconds >= (settings.inactivity_seconds || 60) && !triggered) {
        setIsOpen(true);
        setTriggered(true);
        trackImpression();
      }
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    // Check inactivity every 5 seconds
    inactivityTimer = setInterval(checkInactivity, 5000);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      clearInterval(inactivityTimer);
    };
  }, [settingsLoaded, settings, handleMouseLeave, triggered, hasItems]);

  const trackImpression = async () => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=impression&trigger_type=checkout_exit`);
    } catch (e) {
      // Silent fail
    }
  };

  const trackConversion = async () => {
    try {
      await axios.post(`${API_URL}/api/popup/track?event_type=conversion&trigger_type=checkout_exit`);
    } catch (e) {
      // Silent fail
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem('checkout_exit_dismissed', 'true');
  };

  const handleContinue = () => {
    trackConversion();
    if (settings?.offer_code && onApplyDiscount) {
      onApplyDiscount(settings.offer_code);
    }
    setIsOpen(false);
    if (onContinue) onContinue();
  };

  if (!isOpen || !settings?.enabled) return null;

  const discountText = settings.offer_type === 'percentage' 
    ? `${settings.offer_value}% OFF` 
    : `$${settings.offer_value} OFF`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 animate-fade-in" data-testid="checkout-exit-popup">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 p-1"
          data-testid="checkout-exit-popup-close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header with urgency */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="h-8 w-8" />
          </div>
          <h2 className="text-2xl font-bold mb-1" data-testid="checkout-exit-headline">
            {settings.headline}
          </h2>
          <p className="text-white/90 text-sm">
            {settings.body}
          </p>
        </div>

        {/* Offer Section */}
        <div className="p-6">
          {settings.offer_value > 0 && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-dashed border-amber-300 rounded-xl p-4 mb-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Gift className="h-5 w-5 text-amber-600" />
                <span className="text-amber-700 font-medium text-sm">SPECIAL OFFER</span>
              </div>
              <p className="text-3xl font-bold text-amber-600 mb-1">
                {discountText}
              </p>
              {settings.offer_code && (
                <p className="text-sm text-gray-600">
                  Use code: <span className="font-mono font-bold bg-amber-100 px-2 py-1 rounded">{settings.offer_code}</span>
                </p>
              )}
            </div>
          )}

          {/* Cart Value Reminder */}
          {cartTotal > 0 && (
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 mb-4">
              <span className="text-gray-600 text-sm">Your cart value:</span>
              <span className="font-bold text-lg text-[#003456]">${cartTotal.toFixed(2)}</span>
            </div>
          )}

          {/* Urgency indicator */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Clock className="h-4 w-4" />
            <span>Limited time offer - don't miss out!</span>
          </div>

          {/* CTA Button */}
          <Button 
            onClick={handleContinue}
            className="w-full bg-[#003456] hover:bg-[#002845] text-white py-4 text-lg font-semibold"
            data-testid="checkout-exit-cta"
          >
            {settings.cta_text}
          </Button>

          <button 
            onClick={handleClose}
            className="w-full mt-3 text-gray-500 text-sm hover:text-gray-700"
          >
            No thanks, I'll leave
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutExitPopup;
